import { get } from "../../api/axios"
import endpoints from "../../api/endpoints"
import { toastApiError } from "../../helpers/apiError"

export const PHOTO_REQUEST_SUBJECT_MAX = 120
export const PHOTO_REQUEST_MESSAGE_MAX = 500
export const PHOTO_REQUEST_PORTFOLIO_MAX = 150

export const PHOTOGRAPHY_CTA = {
  contribute: "Contribute",
  edit: "Edit Request",
  upload: "Upload Media",
}

const REQUEST_PAGE_SIZE = 50
const MAX_REQUEST_PAGES = 20

const sameId = (a, b) => a != null && b != null && String(a) === String(b)

const unwrapPagedList = result => {
  if (Array.isArray(result)) return result
  if (Array.isArray(result?.data)) return result.data
  return []
}

const unwrapEntity = payload => {
  if (!payload || typeof payload !== "object") return null
  if (payload.id) return payload
  if (payload.data?.id) return payload.data
  if (Array.isArray(payload.data) && payload.data[0]?.id) return payload.data[0]
  return null
}

export const isAcceptedPhotoRequest = request => {
  const status = String(request?.status || "").toLowerCase()
  return status === "accepted" || status === "approved"
}

export const isPendingPhotoRequest = request => {
  const status = String(request?.status || "").toLowerCase()
  return (
    status === "pending" ||
    status === "submitted" ||
    status === "in_review" ||
    status === "awaiting_approval"
  )
}

export const isRejectedPhotoRequest = request => {
  const status = String(request?.status || "").toLowerCase()
  return status === "rejected" || status === "declined"
}

export const getPhotographyCtaLabel = request => {
  if (!request?.id || isRejectedPhotoRequest(request)) {
    return PHOTOGRAPHY_CTA.contribute
  }
  if (isAcceptedPhotoRequest(request)) return PHOTOGRAPHY_CTA.upload
  if (isPendingPhotoRequest(request)) return PHOTOGRAPHY_CTA.edit
  return PHOTOGRAPHY_CTA.contribute
}

export const pickCityPhotographyRequest = (
  list,
  { cityId, userId } = {},
) => {
  const matches = (Array.isArray(list) ? list : []).filter(item => {
    if (!item?.id) return false
    if (
      cityId &&
      !sameId(item.city_id, cityId) &&
      !sameId(item.city?.id, cityId)
    ) {
      return false
    }
    if (userId) {
      const ownerId =
        item.user_id ||
        item.user?.id ||
        item.requester_id ||
        item.requester?.id
      if (ownerId && !sameId(ownerId, userId)) return false
    }
    return true
  })

  if (matches.length === 0) return null

  const accepted = matches.find(isAcceptedPhotoRequest)
  if (accepted) return accepted

  const pending = [...matches]
    .filter(isPendingPhotoRequest)
    .sort((a, b) => {
      const aTime = new Date(a.created_at || 0).getTime()
      const bTime = new Date(b.created_at || 0).getTime()
      return bTime - aTime
    })[0]
  if (pending) return pending

  return null
}

export const mapPhotoRequestFormValues = request => ({
  subject: String(request?.subject || "").slice(0, PHOTO_REQUEST_SUBJECT_MAX),
  message: String(request?.message || "").slice(0, PHOTO_REQUEST_MESSAGE_MAX),
  portfolio_link: String(request?.portfolio_link || "").slice(
    0,
    PHOTO_REQUEST_PORTFOLIO_MAX,
  ),
})

export const toPhotoRequestPayload = (values, { cityId, includeCityId } = {}) => {
  const payload = {
    subject: String(values?.subject || "").trim(),
    message: String(values?.message || "").trim(),
    portfolio_link: String(values?.portfolio_link || "").trim(),
  }
  if (includeCityId) payload.city_id = cityId
  return payload
}

export const unwrapPhotoRequestResponse = response =>
  unwrapEntity(response?.data?.result) || unwrapEntity(response?.data)

export const fetchCityPhotographyRequest = async ({
  cityId,
  userId,
  signal,
} = {}) => {
  if (!cityId) return { ok: true, request: null }

  let page = 1
  let lastPage = 1

  do {
    const response = await get(endpoints.getCityPhotographyRequests, {
      params: {
        page,
        per_page: REQUEST_PAGE_SIZE,
        city_id: cityId,
      },
      signal,
    })

    if (signal?.aborted) return { ok: false, aborted: true }

    if (!response) {
      return { ok: false, aborted: Boolean(signal?.aborted) }
    }

    if (!response?.data?.success) {
      toastApiError(response, "Failed to load contribution request")
      return { ok: false }
    }

    const result = response.data.result || {}
    const match = pickCityPhotographyRequest(unwrapPagedList(result), {
      cityId,
      userId,
    })
    if (match) return { ok: true, request: match }

    lastPage = Number(result.last_page || 1)
    page += 1
  } while (page <= lastPage && page <= MAX_REQUEST_PAGES)

  return { ok: true, request: null }
}
